/**
 * ChangelogPopup — Phase 17.3.2
 *
 * In-app version changelog notification.
 * Shows a modal popup with recent changes when the user hasn't seen
 * the latest version. Dismissal is persisted in localStorage.
 *
 * Usage:
 *   <ChangelogPopup />  // auto-shows if new entries exist
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

// ===== Types =====

export interface ChangelogEntry {
  /** Semantic version string, e.g. "0.16.0" */
  version: string;
  /** ISO date string, e.g. "2026-03-20" */
  date: string;
  /** Short title for this release */
  title: string;
  /** List of changes grouped by type */
  changes: ChangelogChange[];
}

export interface ChangelogChange {
  type: 'feature' | 'fix' | 'improvement' | 'breaking';
  text: string;
}

export const CHANGE_TYPE_LABELS: Record<ChangelogChange['type'], { label: string; emoji: string; color: string }> = {
  feature: { label: '新功能', emoji: '✨', color: 'text-green-600 dark:text-green-400' },
  fix: { label: '修复', emoji: '🐛', color: 'text-red-600 dark:text-red-400' },
  improvement: { label: '优化', emoji: '⚡', color: 'text-blue-600 dark:text-blue-400' },
  breaking: { label: '破坏性变更', emoji: '💥', color: 'text-orange-600 dark:text-orange-400' },
};

// ===== Changelog Data =====

/**
 * Changelog entries in reverse chronological order (newest first).
 * Add new entries at the TOP of this array.
 */
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.18.0',
    date: '2026-04-05',
    title: '同人衍生馆',
    changes: [
      { type: 'feature', text: '🌿 全新"同人衍生馆"板块 — 从任意小说的任意章节创建同人分支' },
      { type: 'feature', text: '一键快照 — 系统自动复制原作世界观、人物、设定、正文到独立新小说' },
      { type: 'feature', text: '分支家族树 — 可视化查看一部作品的所有衍生分支关系' },
      { type: 'feature', text: '热门原作 + 最新分支 — 衍生馆首页展示最受欢迎的原作和分支' },
      { type: 'improvement', text: '作品设置新增"允许衍生"开关 — 作者可控制是否允许他人创建同人分支' },
    ],
  },
  {
    version: '0.17.0',
    date: '2026-03-20',
    title: '运营与增长',
    changes: [
      { type: 'feature', text: 'SEO Meta标签 — 全部17个页面动态title/description/OG标签' },
      { type: 'feature', text: '事件分析系统 — 17种事件类型追踪 + 自动批量上报' },
      { type: 'feature', text: '反馈入口 — 浮动反馈按钮 + 分类表单 + Supabase持久化' },
      { type: 'feature', text: '版本更新通知 — 应用内Changelog弹窗' },
    ],
  },
  {
    version: '0.16.0',
    date: '2026-03-20',
    title: '桌面端完善',
    changes: [
      { type: 'feature', text: 'Electron IPC完善 — 11个通道 + 统一桥接层' },
      { type: 'feature', text: '本地数据备份格式 — 序列化/反序列化 + 结构验证' },
      { type: 'improvement', text: 'LLM直连 — 桌面端自动跳过代理直连API' },
    ],
  },
  {
    version: '0.15.0',
    date: '2026-03-20',
    title: '性能与体积优化',
    changes: [
      { type: 'improvement', text: 'Index chunk 482→77KB (-84%)，移除dagre+lodash' },
      { type: 'improvement', text: 'OutlinePage 113→23KB (-80%)，自研轻量treeLayout' },
      { type: 'feature', text: '分页组件 + PlazaPage分页集成' },
      { type: 'improvement', text: '全部查询添加.limit()安全上限' },
      { type: 'feature', text: '22项大数据量压测基准 — 全部通过' },
    ],
  },
  {
    version: '0.14.0',
    date: '2026-03-19',
    title: '写作体验深化',
    changes: [
      { type: 'feature', text: '多模式AI写作 — 续写/润色/扩写/缩写/改写/翻译' },
      { type: 'feature', text: '大纲感知续写 — 章纲/下章预览/节情节线' },
      { type: 'feature', text: '专注写作模式 + 打字机滚动 + Markdown快捷键' },
      { type: 'feature', text: '设定关系图谱 + 时间线可视化' },
      { type: 'feature', text: '批量章节管理 + 批量导出.txt/.epub' },
      { type: 'feature', text: '人物一致性检查 + 文风一致性评分' },
    ],
  },
];

// ===== Pure helpers (exported for testing) =====

const STORAGE_KEY = 'novelforge_last_seen_version';

/**
 * Get the latest changelog version string.
 */
export function getLatestVersion(entries: ChangelogEntry[]): string {
  return entries.length > 0 ? entries[0].version : '0.0.0';
}

/**
 * Read the last seen version from localStorage.
 */
export function getLastSeenVersion(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save the last seen version to localStorage.
 */
export function setLastSeenVersion(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, version);
  } catch {
    // ignore storage errors (e.g., incognito mode quota)
  }
}

/**
 * Compare two semver-like version strings.
 * Returns true if `a` is newer than `b`.
 */
export function isNewerVersion(a: string, b: string): boolean {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return true;
    if (va < vb) return false;
  }
  return false;
}

/**
 * Get changelog entries that are newer than the last seen version.
 */
export function getUnseenEntries(entries: ChangelogEntry[], lastSeen: string | null): ChangelogEntry[] {
  if (!lastSeen) return entries.slice(0, 3); // First visit: show last 3
  return entries.filter((e) => isNewerVersion(e.version, lastSeen));
}

/**
 * Check if the changelog popup should auto-show.
 */
export function shouldAutoShow(entries: ChangelogEntry[]): boolean {
  const lastSeen = getLastSeenVersion();
  const latest = getLatestVersion(entries);
  if (!lastSeen) return true; // First visit
  return isNewerVersion(latest, lastSeen);
}

// ===== Component =====

export function ChangelogPopup() {
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  // Auto-show on mount if there are unseen entries
  useEffect(() => {
    if (shouldAutoShow(CHANGELOG)) {
      setOpen(true);
    }
  }, []);

  const displayEntries = useMemo(() => {
    if (manualOpen) return CHANGELOG.slice(0, 5); // Manual: show last 5
    const lastSeen = getLastSeenVersion();
    return getUnseenEntries(CHANGELOG, lastSeen);
  }, [manualOpen]);

  const handleDismiss = useCallback(() => {
    const latest = getLatestVersion(CHANGELOG);
    setLastSeenVersion(latest);
    setOpen(false);
    setManualOpen(false);
  }, []);

  const handleManualOpen = useCallback(() => {
    setManualOpen(true);
    setOpen(true);
  }, []);

  // Keyboard dismiss
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, handleDismiss]);

  return (
    <>
      {/* Manual trigger — small button in footer area */}
      <button
        type="button"
        onClick={handleManualOpen}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        aria-label="查看更新日志"
        title="查看更新日志"
      >
        v{getLatestVersion(CHANGELOG)}
      </button>

      {/* Modal overlay */}
      {open && displayEntries.length > 0 && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={handleDismiss}
            data-testid="changelog-backdrop"
          />
          <div
            className="fixed inset-x-4 top-[10vh] z-50 mx-auto max-w-lg rounded-2xl bg-card border border-border shadow-2xl overflow-hidden sm:inset-x-auto sm:w-[480px]"
            role="dialog"
            aria-label="版本更新日志"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/50">
              <div>
                <h2 className="text-base font-bold">🎉 更新日志</h2>
                <p className="text-xs text-muted-foreground mt-0.5">看看有什么新变化</p>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="关闭"
              >
                <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            {/* Entries */}
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-6">
              {displayEntries.map((entry) => (
                <div key={entry.version}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-sm font-bold text-primary">v{entry.version}</span>
                    <span className="text-xs text-muted-foreground">{entry.date}</span>
                    <span className="text-xs font-medium text-foreground/80">— {entry.title}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {entry.changes.map((change, i) => {
                      const { emoji, color } = CHANGE_TYPE_LABELS[change.type];
                      return (
                        <li key={`change-${i}`} className="flex items-start gap-2 text-sm">
                          <span className={`shrink-0 ${color}`}>{emoji}</span>
                          <span className="text-foreground/90">{change.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-border bg-muted/30">
              <button
                type="button"
                onClick={handleDismiss}
                className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                知道了 👍
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
